import React from 'react';
import { CircleUser as UserCircle, Calendar, Shield, Briefcase, Trash2, FileText, MessageSquare } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Badge } from './Badge';
import { Button } from './Button';
import { ROUTES } from '../constants';

interface AccountSidebarProps {
  name: string;
  email: string;
  accountType: string;
  memberSince: string;
  theme?: 'blue' | 'green';
  verified?: boolean;
  onDeleteAccount?: () => void;
  userRole?: 'user' | 'provider';
}

export const AccountSidebar: React.FC<AccountSidebarProps> = ({
  name,
  email,
  accountType,
  memberSince,
  theme = 'blue',
  verified = false,
  onDeleteAccount,
  userRole,
}) => {
  const navigate = useNavigate();
  const themeColors = {
    blue: {
      bg: 'bg-blue-50',
      icon: 'text-blue-600',
      badge: 'bg-blue-100 text-blue-700',
    },
    green: {
      bg: 'bg-green-50',
      icon: 'text-green-600',
      badge: 'bg-green-100 text-green-700',
    },
  };

  const colors = themeColors[theme];

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="text-center mb-6">
        <div className={`inline-flex items-center justify-center w-20 h-20 ${colors.bg} rounded-full mb-4`}>
          {theme === 'green' ? (
            <Briefcase className={`h-10 w-10 ${colors.icon}`} />
          ) : (
            <UserCircle className={`h-10 w-10 ${colors.icon}`} />
          )}
        </div>
        <h3 className="text-lg font-bold text-gray-900 mb-1">{name}</h3>
        <p className="text-sm text-gray-600 mb-3">{email}</p>
        {verified && (
          <div className="flex justify-center items-center gap-1">
            <Shield className="h-3 w-3 text-green-600" />
            <Badge variant="success">
              {theme === 'green' ? 'CQC Verified' : 'Verified'}
            </Badge>
          </div>
        )}
      </div>

      <div className="space-y-4 border-t border-gray-200 pt-6">
        <div>
          <p className="text-xs text-gray-500 mb-1">Account Type</p>
          <div className="flex items-center gap-2">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${colors.badge}`}>
              {accountType}
            </span>
          </div>
        </div>

        <div>
          <p className="text-xs text-gray-500 mb-1">Member Since</p>
          <div className="flex items-center gap-2 text-sm text-gray-700">
            <Calendar className="h-4 w-4 text-gray-400" />
            {memberSince}
          </div>
        </div>
      </div>

      <div className="border-t border-gray-200 pt-4 mt-4 space-y-2">
        <Button
          variant="outline"
          className="w-full justify-start"
          onClick={() => navigate(ROUTES.MESSAGES)}
        >
          <MessageSquare className="h-4 w-4 mr-2" />
          Messages
        </Button>
        {userRole === 'user' && (
          <Button
            variant="outline"
            className="w-full justify-start"
            onClick={() => navigate(ROUTES.MY_POSTS)}
          >
            <FileText className="h-4 w-4 mr-2" />
            My Posts
          </Button>
        )}
        {userRole === 'provider' && (
          <Button
            variant="outline"
            className="w-full justify-start"
            onClick={() => navigate(ROUTES.ALL_POSTS)}
          >
            <FileText className="h-4 w-4 mr-2" />
            Browse Care Requests
          </Button>
        )}
        {onDeleteAccount && (
          <Button
            variant="outline"
            className="w-full justify-start text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
            onClick={onDeleteAccount}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete Account
          </Button>
        )}
      </div>
    </div>
  );
};
